import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkles, RefreshCw, Loader2, AlertCircle, ArrowUpRight, Brain, ChevronDown, Check
} from 'lucide-react';
import { workspaceAPI } from '../api';

/**
 * WorkspaceIntelligence — research-assistant briefing pinned above the empty chat.
 *
 * Behaviour:
 *  - Auto-loads cached intelligence on mount.
 *  - Polls every 4s while status === 'generating' (cap: 60 polls / 4 minutes).
 *  - Shows skeleton while pending/generating.
 *  - One-click question pills wire into the chat send handler via `onAskQuestion(text)`.
 *  - Manual Refresh button forces a regeneration (synchronous backend call).
 *
 * Self-contained: no global state, no toasts; integrates via two props only.
 */
const STATUS_GENERATING = ['pending', 'generating'];
const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 60;
const WI_CACHE_PREFIX = 'bkw_wi_';

// SessionStorage helpers — keep the briefing instantly readable on every workspace mount
// without waiting for the network. We only cache ready/failed states (never `generating`,
// since stale "generating" would leave the UI stuck in skeleton until the next refresh).
function readCachedWI(workspaceId) {
  try {
    const raw = sessionStorage.getItem(WI_CACHE_PREFIX + workspaceId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.status) return null;
    return parsed;
  } catch (e) { return null; }
}
function writeCachedWI(workspaceId, data) {
  try {
    if (!data || !workspaceId) return;
    // Only cache settled states; don't persist transient generating placeholders.
    if (STATUS_GENERATING.includes(data.status)) return;
    sessionStorage.setItem(WI_CACHE_PREFIX + workspaceId, JSON.stringify(data));
  } catch (e) { /* quota exceeded or disabled — silent */ }
}

export default function WorkspaceIntelligence({ workspaceId, onAskQuestion, defaultCollapsed = false }) {
  // Initialise from sessionStorage so the briefing renders instantly on mount and
  // doesn't flash a skeleton on every workspace re-entry — refresh happens in the
  // background via fetchOnce().
  const [data, setData] = useState(() => readCachedWI(workspaceId));
  const [loading, setLoading] = useState(() => !readCachedWI(workspaceId));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // Tracks indices of question pills that have already been clicked — disables them so a
  // double-tap or re-render can't fire `onAskQuestion` twice for the same prompt.
  const [submittedIdx, setSubmittedIdx] = useState(() => new Set());
  const pollAttemptsRef = useRef(0);
  const pollTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Sync collapse state when the parent's hint changes (e.g. user navigates between sessions).
  useEffect(() => { setCollapsed(defaultCollapsed); }, [defaultCollapsed]);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await workspaceAPI.getIntelligence(workspaceId);
      if (!isMountedRef.current) return null;
      setData(r.data);
      setError(null);
      writeCachedWI(workspaceId, r.data);  // persist for next mount
      return r.data;
    } catch (e) {
      if (!isMountedRef.current) return null;
      console.error('Intelligence fetch failed:', e);
      setError(e.response?.data?.detail || 'Failed to load workspace intelligence');
      return null;
    }
  }, [workspaceId]);

  // Initial load + polling lifecycle
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    fetchOnce().finally(() => {
      if (isMountedRef.current) setLoading(false);
    });
    return () => {
      isMountedRef.current = false;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [fetchOnce]);

  // Poll while backend is generating
  useEffect(() => {
    const status = data?.status;
    if (!STATUS_GENERATING.includes(status)) {
      pollAttemptsRef.current = 0;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }
    if (pollAttemptsRef.current >= POLL_MAX_ATTEMPTS) return undefined;
    pollTimerRef.current = setTimeout(async () => {
      pollAttemptsRef.current += 1;
      await fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [data?.status, data?.updated_at, fetchOnce]);

  const handleRegenerate = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await workspaceAPI.regenerateIntelligence(workspaceId);
      if (isMountedRef.current) {
        setData((prev) => {
          const merged = { ...(prev || {}), ...r.data, has_sources: true, is_stale: false };
          writeCachedWI(workspaceId, merged);
          return merged;
        });
        setError(null);
      }
    } catch (e) {
      console.error('Intelligence regen failed:', e);
      if (isMountedRef.current) {
        setError(e.response?.data?.detail || 'Failed to regenerate workspace intelligence');
      }
    } finally {
      if (isMountedRef.current) setRefreshing(false);
    }
  }, [workspaceId, refreshing]);

  // Don't show anything if there are no sources yet — let the EmptyChat handle the welcome.
  if (loading) {
    return (
      <div className="rounded-2xl border border-[#E5E7EB] bg-gradient-to-br from-[#F8FAFF] to-white p-5" data-testid="wi-loading">
        <SkeletonShimmer />
      </div>
    );
  }
  if (!data?.has_sources) return null;

  const isGenerating = STATUS_GENERATING.includes(data.status) && pollAttemptsRef.current < POLL_MAX_ATTEMPTS;
  const isFailed = data.status === 'failed';
  const overview = data.overview;
  const questions = Array.isArray(data.suggested_questions) ? data.suggested_questions : [];

  return (
    <div
      className="rounded-2xl border border-[#E5E7EB] bg-gradient-to-br from-[#F8FAFF] via-white to-white shadow-sm overflow-hidden"
      data-testid="workspace-intelligence-panel"
    >
      <Header
        isGenerating={isGenerating}
        isStale={!!data.is_stale}
        onRegenerate={handleRegenerate}
        refreshing={refreshing}
        provider={data.provider}
        model={data.model}
        generatedAt={data.generated_at}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(c => !c)}
      />

      {!collapsed && (
        <>
          {isFailed && !isGenerating && (
            <FailureNotice errorMessage={data.error_message} onRetry={handleRegenerate} retrying={refreshing} />
          )}

          {error && (
            <div className="mx-5 mb-3 flex items-start gap-2 text-xs text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-lg px-3 py-2" data-testid="wi-fetch-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!overview && isGenerating && <GeneratingState />}

          {overview && (
            <div className="px-5 pb-1">
              <div
                className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-[#111827] prose-p:text-[#374151] prose-p:leading-relaxed prose-strong:text-[#111827] prose-strong:font-semibold prose-a:text-[#6590FF]"
                data-testid="wi-overview"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{overview}</ReactMarkdown>
              </div>
            </div>
          )}

          {questions.length > 0 && (
            <SuggestedQuestions
              questions={questions}
              onAskQuestion={(q, idx) => {
                if (submittedIdx.has(idx)) return;  // double-click guard
                setSubmittedIdx(prev => new Set(prev).add(idx));
                onAskQuestion?.(q);
              }}
              disabled={isGenerating}
              submittedIdx={submittedIdx}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ---------- subcomponents ---------- */

function Header({ isGenerating, isStale, onRegenerate, refreshing, provider, model, generatedAt, collapsed, onToggleCollapse }) {
  return (
    <div className="flex items-center justify-between px-5 pt-4 pb-3">
      <button
        type="button"
        onClick={onToggleCollapse}
        className="flex items-center gap-2.5 min-w-0 flex-1 text-left rounded-lg hover:bg-white/60 transition-colors -mx-1 px-1 py-0.5"
        data-testid="wi-toggle-collapse"
        aria-expanded={!collapsed}
      >
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#6590FF] to-[#4F46E5] flex items-center justify-center shadow-sm flex-shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-[#111827] leading-tight flex items-center gap-1.5" style={{ fontFamily: 'var(--font-heading)' }}>
            Workspace Intelligence
            {collapsed && <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF] -rotate-90" />}
            {!collapsed && <ChevronDown className="w-3.5 h-3.5 text-[#9CA3AF]" />}
          </h3>
          <p className="text-[10.5px] text-[#6B7280] truncate" data-testid="wi-meta">
            {isGenerating ? (
              <span className="text-[#6590FF] font-medium">Analysing your sources…</span>
            ) : isStale ? (
              <span className="text-[#B45309] font-medium">New sources added — click refresh for an updated briefing.</span>
            ) : generatedAt ? (
              <>Generated {formatTimeAgo(generatedAt)}{model ? ` · ${friendlyModel(provider, model)}` : ''}</>
            ) : (
              'Synthesised from your uploaded sources'
            )}
          </p>
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
        disabled={refreshing || isGenerating}
        className="flex-shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium text-[#4B5563] hover:text-[#111827] hover:bg-[#F3F4F6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="wi-refresh-btn"
        title="Regenerate intelligence"
      >
        {refreshing || isGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        Refresh
      </button>
    </div>
  );
}

function SuggestedQuestions({ questions, onAskQuestion, disabled, submittedIdx }) {
  return (
    <div className="border-t border-[#F3F4F6] bg-white px-5 py-3.5" data-testid="wi-suggested-questions">
      <div className="flex items-center gap-1.5 mb-2.5">
        <Brain className="w-3.5 h-3.5 text-[#6590FF]" />
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-[#6B7280]">
          Suggested next questions
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {questions.map((q, idx) => {
          const wasSubmitted = submittedIdx?.has(idx);
          return (
            <button
              key={`q-${idx}-${q.slice(0, 40)}`}
              type="button"
              onClick={() => !disabled && !wasSubmitted && onAskQuestion?.(q, idx)}
              disabled={disabled || !onAskQuestion || wasSubmitted}
              className="group flex items-center justify-between gap-3 w-full text-left px-3.5 py-2.5 rounded-xl border border-[#E5E7EB] bg-white hover:bg-[#F8FAFF] hover:border-[#6590FF]/40 hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-[#E5E7EB] disabled:hover:shadow-none"
              data-testid={`wi-question-${idx}`}
            >
              <span className="text-[13px] text-[#374151] leading-snug group-hover:text-[#111827]">
                {q}
              </span>
              {wasSubmitted ? (
                <Check className="w-3.5 h-3.5 text-[#059669] flex-shrink-0" strokeWidth={3} />
              ) : (
                <ArrowUpRight className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#6590FF] flex-shrink-0 transition-colors" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GeneratingState() {
  return (
    <div className="px-5 pb-5" data-testid="wi-generating">
      <div className="flex items-center gap-2 text-xs text-[#6590FF] mb-3">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Reading sources, identifying themes, and synthesising findings…</span>
      </div>
      <SkeletonShimmer lines={5} />
    </div>
  );
}

function FailureNotice({ errorMessage, onRetry, retrying }) {
  return (
    <div className="mx-5 mb-3 flex items-start gap-2.5 px-3.5 py-3 rounded-xl border border-[#FDE68A] bg-[#FEFCE8]" data-testid="wi-failure">
      <AlertCircle className="w-4 h-4 text-[#B45309] mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-medium text-[#92400E]">Intelligence generation didn&apos;t complete.</p>
        {errorMessage && (
          <p className="text-[11px] text-[#92400E]/80 mt-0.5 line-clamp-2">{errorMessage}</p>
        )}
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="mt-2 inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium bg-white border border-[#FDE68A] text-[#92400E] hover:bg-[#FEF3C7] disabled:opacity-50 transition-colors"
          data-testid="wi-retry-btn"
        >
          {retrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Try again
        </button>
      </div>
    </div>
  );
}

function SkeletonShimmer({ lines = 4 }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={`sk-${i}`}
          className="h-3 rounded bg-gradient-to-r from-[#F3F4F6] via-[#E5E7EB] to-[#F3F4F6]"
          style={{ width: `${85 - (i * 5)}%` }}
        />
      ))}
    </div>
  );
}

/* ---------- helpers ---------- */

function formatTimeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(t.getTime())) return '';
  const diffMs = Date.now() - t.getTime();
  const sec = Math.max(0, Math.floor(diffMs / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return t.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function friendlyModel(provider, model) {
  if (!model) return '';
  const m = String(model).toLowerCase();
  if (m.includes('claude')) return 'Claude';
  if (m.includes('gpt-4o')) return 'GPT-4o';
  if (m.includes('gpt-5')) return 'GPT-5';
  if (m.includes('gpt')) return 'GPT';
  if (m.includes('gemini')) return 'Gemini';
  if (m.includes('llama')) return 'Llama';
  return provider ? provider[0].toUpperCase() + provider.slice(1) : model;
}
